import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

const privacyFields = [
  'sales_tax_id',
  'ein',
  'username',
  'password',
  'password_metadata',
  'routing_number',
  'account_number',
  'pin',
  'cdtfa_third_party_access_security_code',
  'ssn',
  'dl',
] as const;

export type PrivacyType = (typeof privacyFields)[number];

// eslint-disable-next-line react-refresh/only-export-components
export const isPrivacyField = (fieldName: string): fieldName is PrivacyType => {
  if (privacyFields.includes(fieldName as PrivacyType)) {
    return true;
  }
  return fieldName.endsWith('_ssn') || fieldName.endsWith('_dl');
};

interface PrivacyContextType {
  isPrivacyVisible: boolean;
  hasPrivacyOnPage: boolean;
  /** Bumps on route change so registrars re-run after presence is cleared. */
  privacyRouteKey: number;
  togglePrivacyVisibility: () => void;
  registerPrivacy: () => void;
}

const PrivacyContext = createContext<PrivacyContextType | undefined>(undefined);

interface PrivacyProviderProps {
  children: ReactNode;
}

export const PrivacyProvider: React.FC<PrivacyProviderProps> = ({ children }) => {
  const [isPrivacyVisible, setIsPrivacyVisible] = useState(false);
  const [hasPrivacyOnPage, setHasPrivacyOnPage] = useState(false);
  const [privacyRouteKey, setPrivacyRouteKey] = useState(0);
  const [prevPathname, setPrevPathname] = useState<string | null>(null);
  const location = useLocation();

  // Reset during render (before children commit) so a parent useEffect cannot
  // overwrite child registerPrivacy calls. See React "adjusting state when a
  // prop changes" — KA-2076 eye-toggle race after refresh/navigation.
  if (prevPathname !== location.pathname) {
    setPrevPathname(location.pathname);
    setHasPrivacyOnPage(false);
    setIsPrivacyVisible(false);
    setPrivacyRouteKey(key => key + 1);
  }

  const togglePrivacyVisibility = useCallback(() => {
    setIsPrivacyVisible(visible => !visible);
  }, []);

  const registerPrivacy = useCallback(() => {
    setHasPrivacyOnPage(true);
  }, []);

  const value = useMemo(
    () => ({
      isPrivacyVisible,
      hasPrivacyOnPage,
      privacyRouteKey,
      togglePrivacyVisibility,
      registerPrivacy,
    }),
    [isPrivacyVisible, hasPrivacyOnPage, privacyRouteKey, togglePrivacyVisibility, registerPrivacy]
  );

  return <PrivacyContext.Provider value={value}>{children}</PrivacyContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const usePrivacy = (): PrivacyContextType => {
  const context = useContext(PrivacyContext);
  if (context === undefined) {
    throw new Error('usePrivacy must be used within a PrivacyProvider');
  }
  return context;
};
